import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ticketsApi } from '../api/tickets';
import { aiApi } from '../api/ai';
import { categoriesApi } from '../api/categories';
import { useClassifications } from '../hooks/useClassifications';
import { useTicketFormLayout } from '../lib/ticketFormLayout';
import { fieldHelp } from '../lib/fieldHelp';
import { teamsApi } from '../api/teams';
import { companiesApi } from '../api/companies';
import { crmOrgsApi, crmContactsApi } from '../api/crm';
import { productLinesApi, BUILTIN_FIELDS, type ProductLine, type FieldDefinition } from '../api/productLines';
import { taskTemplatesApi } from '../api/taskTemplates';
import { useCompany } from '../contexts/CompanyContext';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionsContext';
import { Sparkles, Loader2, AlertTriangle, X, ChevronDown, Columns, Paperclip, Wrench, ListChecks, HelpCircle } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import FileUploader from '../components/FileUploader';
import { insertUploadingPlaceholder, resolveUploadingPlaceholder, hasPendingInlineUploads } from '../lib/inlineImages';
import AssigneePicker from '../components/tickets/AssigneePicker';
import NewContactForm from '../components/tickets/NewContactForm';
import QuickAdd from '../components/QuickAdd';
import { addCreatedToCache, invalidateCrm } from '../lib/queryCache';
import { readFormDraft, clearFormDraft, usePersistFormDraft } from '../hooks/useFormDraft';
import type { UploadedAttachment } from '../api/attachments';
import type {
  TicketPriority,
  TicketType,
  TicketImpact,
  TicketEnvironment,
  TicketSource,
} from '../types';

// Type / Priority / Source / Business impact / Environment are per-company
// pick-lists (Company Settings -> Classification), read through
// useClassifications. Nothing to hardcode here any more.

/** A labelled field. The label carries its own hover explanation when
 *  fieldHelp has one — the little ? is there so people know to hover. */
function FieldGroup({ label, children, required }: { label: string; children: React.ReactNode; required?: boolean }) {
  const help = fieldHelp(label);
  return (
    <div>
      <label className="label" title={help}>
        {label}
        {required && <span className="text-err ml-0.5">*</span>}
        {help && <HelpCircle className="w-3 h-3 inline-block ml-1 -mt-0.5 opacity-50" aria-hidden />}
      </label>
      {children}
    </div>
  );
}

function CustomFieldInput({
  def,
  value,
  onChange,
  isRequired,
}: {
  def: FieldDefinition;
  value: string;
  onChange: (v: string) => void;
  isRequired: boolean;
}) {
  const opts = def.options ?? [];
  switch (def.fieldType) {
    case 'textarea':
      return (
        <FieldGroup label={def.label} required={isRequired}>
          {def.helpText && <p className="text-xs text-muted mb-1">{def.helpText}</p>}
          <textarea className="input min-h-[80px] resize-y" placeholder={def.placeholder ?? ''} value={value} onChange={e => onChange(e.target.value)} required={isRequired} />
        </FieldGroup>
      );
    case 'select':
      return (
        <FieldGroup label={def.label} required={isRequired}>
          {def.helpText && <p className="text-xs text-muted mb-1">{def.helpText}</p>}
          <select className="select" value={value} onChange={e => onChange(e.target.value)} required={isRequired}>
            <option value="">Select…</option>
            {opts.map((o: any) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </FieldGroup>
      );
    case 'boolean':
      return (
        <FieldGroup label={def.label}>
          {def.helpText && <p className="text-xs text-muted mb-1">{def.helpText}</p>}
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" className="w-4 h-4 accent-[var(--scpa-primary)]" checked={value === 'true'} onChange={e => onChange(e.target.checked ? 'true' : 'false')} />
            <span className="text-sm">{def.placeholder || def.label}</span>
          </label>
        </FieldGroup>
      );
    case 'number':
      return (
        <FieldGroup label={def.label} required={isRequired}>
          {def.helpText && <p className="text-xs text-muted mb-1">{def.helpText}</p>}
          <input type="number" className="input" placeholder={def.placeholder ?? ''} value={value} onChange={e => onChange(e.target.value)} required={isRequired} />
        </FieldGroup>
      );
    case 'date':
      return (
        <FieldGroup label={def.label} required={isRequired}>
          {def.helpText && <p className="text-xs text-muted mb-1">{def.helpText}</p>}
          <input type="date" className="input" value={value} onChange={e => onChange(e.target.value)} required={isRequired} />
        </FieldGroup>
      );
    case 'url':
      return (
        <FieldGroup label={def.label} required={isRequired}>
          {def.helpText && <p className="text-xs text-muted mb-1">{def.helpText}</p>}
          <input type="url" className="input" placeholder={def.placeholder ?? 'https://'} value={value} onChange={e => onChange(e.target.value)} required={isRequired} />
        </FieldGroup>
      );
    default:
      return (
        <FieldGroup label={def.label} required={isRequired}>
          {def.helpText && <p className="text-xs text-muted mb-1">{def.helpText}</p>}
          <input type="text" className="input" placeholder={def.placeholder ?? ''} value={value} onChange={e => onChange(e.target.value)} required={isRequired} />
        </FieldGroup>
      );
  }
}

// ─── Collapsible section wrapper (sidebar only now) ─────────────────────────

/** Guided-layout steps, in order. */
type StepId = 'details' | 'classify' | 'people' | 'more' | 'notify';

interface CollapsibleSectionProps {
  id: string;
  title: string;
  children: React.ReactNode;
  isOpen: boolean;
  onToggle: (id: string, open: boolean) => void;
  badge?: number;
}

function CollapsibleSection({
  id,
  title,
  children,
  isOpen,
  onToggle,
  badge,
}: CollapsibleSectionProps) {
  return (
    <div className="rounded-xl border border-line bg-surface overflow-hidden">
      <button
        type="button"
        className="w-full px-4 py-2.5 border-b border-line-light flex items-center justify-between hover:bg-row-odd transition-colors"
        onClick={() => onToggle(id, !isOpen)}
      >
        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted">{title}</p>
        <div className="flex items-center gap-1.5">
          {badge !== undefined && badge > 0 && (
            <span className="text-[10px] bg-primary-muted text-primary rounded-full px-1.5 py-0.5 font-medium">{badge}</span>
          )}
          <ChevronDown className={`w-3.5 h-3.5 text-muted transition-transform duration-200 ${isOpen ? '' : '-rotate-90'}`} />
        </div>
      </button>
      {isOpen && <div className="p-4 space-y-3">{children}</div>}
    </div>
  );
}

/** Is there anything in this form worth keeping? Untouched defaults don't
 *  count — they'd make every second visit claim to have restored a draft. */
function hasContent(v: Record<string, any>): boolean {
  return Boolean(
    v.title?.trim() ||
    v.description?.trim() ||
    v.tags?.length ||
    v.attachments?.length ||
    v.assigneeIds?.length ||
    v.ccList?.length ||
    Object.values(v.customFieldValues ?? {}).some(Boolean) ||
    v.categoryId || v.productLineId || v.assignedTeamId || v.contactId ||
    v.crmOrganizationId || v.crmContactId ||
    v.reporterEmail?.trim() || v.reporterName?.trim() ||
    v.product?.trim() || v.productVersion?.trim() || v.department?.trim() ||
    v.location?.trim() || v.dueDate || v.estimatedHours ||
    v.browser?.trim() || v.device?.trim() || v.operatingSystem?.trim() ||
    v.reproducibility || v.stepsToReproduce?.trim() ||
    v.impact || v.environment,
  );
}

// ─── Main component ────────────────────────────────────────────────────────────

export default function TicketCreate() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentCompany } = useCompany();
  const { user } = useAuth();
  const { can, isStaff, isAdmin } = usePermissions();

  const companyId = (currentCompany as any)?.id ?? (currentCompany as any)?.uuid;

  // Draft — everything below is mirrored to sessionStorage so a remount, a
  // reload, or a detour to another screen never costs you what you typed.
  // Keyed per company so a draft can't surface under a different tenant.
  const draftKey = `ctticketing:ticket-draft:${companyId ?? 'pending'}`;
  const [draft] = useState(() => readFormDraft<Record<string, any>>(draftKey));
  const [draftRestored, setDraftRestored] = useState(() => hasContent(draft));

  // ── Form state ──────────────────────────────────────────────────────────────
  const [title, setTitle]             = useState(draft.title ?? '');
  const [description, setDescription] = useState<string>(draft.description ?? '');
  /** For inserting inline-image references at the caret on paste. */
  const descriptionRef = useRef<HTMLTextAreaElement>(null);
  const [priority, setPriority]       = useState<TicketPriority>(draft.priority ?? 'normal');
  const [type, setType]               = useState<TicketType>(draft.type ?? 'incident');
  const [categoryId, setCategoryId]   = useState(draft.categoryId ?? '');
  const [tags, setTags]               = useState<string[]>(draft.tags ?? []);
  const [source, setSource]           = useState<TicketSource>(draft.source ?? 'in_app');
  const [impact, setImpact]           = useState<TicketImpact | ''>(draft.impact ?? '');
  const [environment, setEnvironment] = useState<TicketEnvironment | ''>(draft.environment ?? '');
  const [product, setProduct]         = useState(draft.product ?? '');
  const [productVersion, setProductVersion] = useState(draft.productVersion ?? '');
  const [department, setDepartment]   = useState(draft.department ?? '');
  const [location, setLocation]       = useState(draft.location ?? '');
  const [dueDate, setDueDate]         = useState(draft.dueDate ?? '');
  const [estimatedHours, setEstimatedHours] = useState(draft.estimatedHours ?? '');

  // Per-company pick-lists (Company Settings -> Classification). `current` is
  // passed so a draft carrying a value that has since been turned off still
  // shows what it holds rather than silently displaying something else.
  const { optionsFor, defaultKeyOf, isLoading: classificationsLoading } = useClassifications();
  const typeOptions     = optionsFor('type', type);
  const priorityOptions = optionsFor('priority', priority);
  const sourceOptions   = optionsFor('source', source);
  const impactOptions   = optionsFor('impact', impact);
  const envOptions      = optionsFor('environment', environment);

  // Seed the required fields from the company's defaults once the lists load.
  // Only replaces a value this company cannot actually use — a draft or a
  // deliberate pick is left alone.
  const classificationsSeeded = useRef(false);
  useEffect(() => {
    if (classificationsLoading || classificationsSeeded.current) return;
    if (typeOptions.length === 0 && priorityOptions.length === 0) return;
    classificationsSeeded.current = true;
    const usable = (opts: { key: string }[], v: string) => opts.some(o => o.key === v);
    if (!usable(typeOptions, type)) setType(defaultKeyOf('type'));
    if (!usable(priorityOptions, priority)) setPriority(defaultKeyOf('priority'));
    // A staff-filed ticket is in_app by nature; fall back only if it is off.
    if (!usable(sourceOptions, source)) setSource(defaultKeyOf('source'));
    if (impact && !usable(impactOptions, impact)) setImpact('');
    if (environment && !usable(envOptions, environment)) setEnvironment('');
  }, [classificationsLoading, typeOptions, priorityOptions, sourceOptions, impactOptions, envOptions,
      type, priority, source, impact, environment, defaultKeyOf]);
  const [browser, setBrowser]         = useState(draft.browser ?? '');
  const [device, setDevice]           = useState(draft.device ?? '');
  const [operatingSystem, setOperatingSystem] = useState(draft.operatingSystem ?? '');
  const [reproducibility, setReproducibility] = useState(draft.reproducibility ?? '');
  const [stepsToReproduce, setStepsToReproduce] = useState(draft.stepsToReproduce ?? '');
  const [assigneeIds, setAssigneeIds]     = useState<string[]>(draft.assigneeIds ?? []); // first = primary
  const [assignedTeamId, setAssignedTeamId] = useState(draft.assignedTeamId ?? '');
  const [contactId, setContactId]     = useState(draft.contactId ?? '');
  const [tagInput, setTagInput]       = useState('');
  const [duplicates, setDuplicates]   = useState<any[]>([]);
  const [productLineId, setProductLineId] = useState(draft.productLineId ?? '');
  // Sub-ticket checklist: 'auto' = whatever template matches the type /
  // product line (the server decides), 'none' = no checklist, else a template id.
  const [taskTemplateChoice, setTaskTemplateChoice] = useState<string>(draft.taskTemplateChoice ?? 'auto');
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, string>>(draft.customFieldValues ?? {});
  const [attachments, setAttachments] = useState<UploadedAttachment[]>(draft.attachments ?? []);
  // Reporter, organization, CCs, notification preference
  const [crmOrganizationId, setCrmOrganizationId] = useState(draft.crmOrganizationId ?? '');
  const [crmContactId, setCrmContactId]   = useState(draft.crmContactId ?? '');
  const [reporterEmail, setReporterEmail] = useState(draft.reporterEmail ?? '');
  const [reporterName, setReporterName]   = useState(draft.reporterName ?? '');
  const [notifyCustomer, setNotifyCustomer] = useState<boolean>(draft.notifyCustomer ?? true);
  const [notifyCreator, setNotifyCreator] = useState<boolean>(draft.notifyCreator ?? false);
  const [emailIncludeContent, setEmailIncludeContent] = useState<boolean>(draft.emailIncludeContent ?? false);
  /** Until the box is touched it follows the per-member default (below). */
  const [emailIncludeContentTouched, setEmailIncludeContentTouched] = useState<boolean>(draft.emailIncludeContentTouched ?? false);
  const [ccList, setCcList] = useState<{ email: string; name?: string }[]>(draft.ccList ?? []);
  const [ccEmailInput, setCcEmailInput]   = useState('');
  const [ccNameInput, setCcNameInput]     = useState('');
  const [showNewContact, setShowNewContact] = useState(false);

  const draftValues = {
    title, description, priority, type, categoryId, tags, source, impact, environment,
    product, productVersion, department, location, dueDate, estimatedHours,
    browser, device, operatingSystem, reproducibility, stepsToReproduce,
    assigneeIds, assignedTeamId, contactId, productLineId, customFieldValues, attachments,
    crmOrganizationId, crmContactId, reporterEmail, reporterName, notifyCustomer,
    notifyCreator, emailIncludeContent, emailIncludeContentTouched, ccList, taskTemplateChoice,
  };
  usePersistFormDraft(draftKey, draftValues, hasContent(draftValues));

  const resetForm = useCallback(() => {
    clearFormDraft(draftKey);
    setDraftRestored(false);
    setTitle(''); setDescription(''); setPriority(defaultKeyOf('priority')); setType(defaultKeyOf('type'));
    setCategoryId(''); setTags([]); setSource('in_app'); setImpact(''); setEnvironment('');
    setProduct(''); setProductVersion(''); setDepartment(''); setLocation('');
    setDueDate(''); setEstimatedHours(''); setBrowser(''); setDevice('');
    setOperatingSystem(''); setReproducibility(''); setStepsToReproduce('');
    setAssigneeIds([]); setAssignedTeamId(''); setContactId(''); setProductLineId('');
    setCustomFieldValues({}); setAttachments([]); setCrmOrganizationId(''); setCrmContactId('');
    setReporterEmail(''); setReporterName(''); setNotifyCustomer(true); setCcList([]);
    setNotifyCreator(false); setEmailIncludeContent(false); setEmailIncludeContentTouched(false);
    setDuplicates([]); setTaskTemplateChoice('auto');
  }, [draftKey]);

  // ── Layout, tab + sidebar collapse state ──────────────────────────────────
  // Two layouts over one set of fields: 'classic' is the original two-column
  // form, 'guided' walks the same fields through numbered steps. The section
  // blocks further down are defined once and placed by whichever layout is
  // on, so the two can never drift apart.
  const [layout, setLayout] = useTicketFormLayout();
  const [step, setStep] = useState<StepId>('details');
  const [activeTab, setActiveTab] = useState<string>('attachments');
  const [validationTab, setValidationTab] = useState<string | null>(null);
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    productLine: true,
    classification: true,
    productSchedule: true,
    assignment: true,
    requester: true,
    notifications: true,
  });

  const toggleSection = useCallback((id: string, open: boolean) => {
    setOpenSections(prev => ({ ...prev, [id]: open }));
  }, []);

  // ── Data queries ────────────────────────────────────────────────────────────
  // Task templates: every active one (for the Checklist picker) and the one
  // that would apply automatically to this type / product line.
  const { data: taskTemplates = [] } = useQuery({
    queryKey: ['task-templates'],
    queryFn: () => taskTemplatesApi.list(),
    enabled: !!currentCompany,
  });
  const { data: matchedTemplate } = useQuery({
    queryKey: ['task-template-match', type, productLineId || null],
    queryFn: () => taskTemplatesApi.match(type, productLineId || null),
    enabled: !!currentCompany,
  });

  const { data: categories = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: () => categoriesApi.list(),
    enabled: !!currentCompany,
  });

  const { data: teams = [] } = useQuery({
    queryKey: ['teams'],
    queryFn: () => teamsApi.list(),
    enabled: !!currentCompany && isStaff,
  });

  const { data: members = [] } = useQuery({
    queryKey: ['company-members', companyId],
    queryFn: () => companiesApi.getMembers(companyId),
    enabled: !!companyId && isStaff,
  });

  // Members flagged "update text in emails by default" (Company Settings →
  // Members) who are on the requester side of this ticket: me as creator, the
  // chosen contact, the reporter email, or a CC. Until the checkbox is touched
  // it follows them; the backend applies the same rule for other channels.
  const contentDefaultMembers = useMemo(() => {
    const myId = (user as any)?.id ?? (user as any)?._id;
    const emails = new Set(
      [reporterEmail, ...ccList.map(c => c.email)].map(e => e.trim().toLowerCase()).filter(Boolean),
    );
    return (members as any[]).filter(m => m.contentEmailsDefault && (
      (myId && m.user?.id === myId)
      || (contactId && m.user?.id === contactId)
      || (m.user?.email && emails.has(String(m.user.email).toLowerCase()))
    ));
  }, [members, user, contactId, reporterEmail, ccList]);
  const effectiveIncludeContent = emailIncludeContentTouched ? emailIncludeContent : contentDefaultMembers.length > 0;

  // Members of each team — for the hover tooltip and the "team" tag in the
  // assignee picker. Team members stay individually selectable (e.g. as the
  // primary assignee); the backend dedupes assignment emails.
  const teamMembersOf = (teamId: string) =>
    (((teams as any[]).find((t: any) => t.id === teamId)?.members ?? []) as any[]);
  const teamMemberNames = (teamId: string) =>
    teamMembersOf(teamId)
      .map((m: any) => m.user?.name ?? m.user?.email)
      .filter(Boolean) as string[];
  const selectedTeamMemberIds = new Set(
    teamMembersOf(assignedTeamId).map((m: any) => m.userId ?? m.user?.id),
  );

  const { data: productLines = [] } = useQuery({
    queryKey: ['product-lines', companyId],
    queryFn: () => productLinesApi.list(),
    enabled: !!currentCompany,
  });

  const { data: orgsData } = useQuery({
    queryKey: ['crm-orgs-picker', companyId],
    queryFn: () => crmOrgsApi.list({ limit: 200 }),
    enabled: !!currentCompany && isStaff,
  });
  const organizations = (orgsData?.organizations || []) as any[];

  const { data: contactsData } = useQuery({
    queryKey: ['crm-contacts-picker', companyId, crmOrganizationId || 'all'],
    queryFn: () => crmContactsApi.list({ limit: 200, ...(crmOrganizationId && { organizationId: crmOrganizationId }) }),
    enabled: !!currentCompany && isStaff,
  });
  const crmContacts = (contactsData?.contacts || []) as any[];

  // Clear contact selection if it doesn't match the picked org
  useEffect(() => {
    if (!crmContactId || !crmOrganizationId) return;
    const selected = crmContacts.find(c => c.id === crmContactId);
    if (selected && selected.organizationId !== crmOrganizationId) setCrmContactId('');
  }, [crmContactId, crmOrganizationId, crmContacts]);

  // Auto-fill reporter fallback fields when picking a CRM contact (only when
  // empty, so we never clobber values the user typed).
  useEffect(() => {
    if (!crmContactId) return;
    const c = crmContacts.find(x => x.id === crmContactId);
    if (!c) return;
    if (c.organizationId && !crmOrganizationId) setCrmOrganizationId(c.organizationId);
    if (!reporterEmail && c.email) setReporterEmail(c.email);
    if (!reporterName && (c.firstName || c.lastName)) {
      setReporterName([c.firstName, c.lastName].filter(Boolean).join(' '));
    }
  }, [crmContactId, crmContacts]); // eslint-disable-line react-hooks/exhaustive-deps

  const addCc = () => {
    const email = ccEmailInput.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('Enter a valid email to CC');
      return;
    }
    if (ccList.some(c => c.email === email)) {
      toast.error('That email is already on the CC list');
      return;
    }
    setCcList([...ccList, { email, name: ccNameInput.trim() || undefined }]);
    setCcEmailInput('');
    setCcNameInput('');
  };
  const removeCc = (email: string) => setCcList(ccList.filter(c => c.email !== email));

  const selectedProductLine = productLineId
    ? (productLines as ProductLine[]).find(pl => pl.id === productLineId) ?? null
    : null;

  const hasProductLines = (productLines as ProductLine[]).length > 0;

  const enabledBuiltinKeys: Set<string> = selectedProductLine
    ? new Set(selectedProductLine.enabledFields as string[])
    // No product line selected → show all built-in fields. Picking a product
    // line narrows them to that line's enabledFields. (Previously this hid all
    // fields until a product line was chosen, which looked like an empty form.)
    : new Set(BUILTIN_FIELDS.map(f => f.key));

  const activeCustomFields = (selectedProductLine?.productLineFields ?? [])
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map(plf => ({ def: plf.fieldDefinition, isRequired: plf.isRequired }));

  const show = (key: string) => enabledBuiltinKeys.has(key);

  // ── Tab definitions ─────────────────────────────────────────────────────────
  const showStaffFields = isStaff && can('assign', 'tickets');
  const showTechSection = !selectedProductLine
    ? (type === 'bug' || type === 'incident')
    : ['browser','device','operatingSystem','reproducibility','stepsToReproduce'].some(k => enabledBuiltinKeys.has(k));

  const showProductSection = !selectedProductLine
    ? true
    : ['product','productVersion','department','location','dueDate','estimatedHours'].some(k => enabledBuiltinKeys.has(k));

  const tabs = [
    { id: 'attachments', label: 'Attachments', icon: Paperclip, badge: attachments.length || undefined },
    ...(showTechSection ? [{ id: 'technical', label: 'Technical', icon: Wrench, badge: undefined }] : []),
    ...(activeCustomFields.length > 0 ? [{ id: 'custom', label: selectedProductLine?.name ?? 'Custom Fields', icon: ListChecks, badge: undefined }] : []),
  ];

  // Ensure activeTab stays valid when tabs change
  const tabIds = tabs.map(t => t.id).join(',');
  useEffect(() => {
    if (!tabs.find(t => t.id === activeTab)) {
      setActiveTab(tabs[0]?.id ?? 'attachments');
    }
  }, [tabIds]);

  // Switch to tab flagged by validation
  useEffect(() => {
    if (validationTab) {
      setActiveTab(validationTab);
      setValidationTab(null);
    }
  }, [validationTab]);

  // ── Mutations ───────────────────────────────────────────────────────────────
  const aiSuggestMutation = useMutation({
    mutationFn: async () => {
      const text = `${title}\n\n${description}`.trim();
      if (!text) throw new Error('Enter title and description first');
      const [categorizeResult, dupes] = await Promise.all([
        aiApi.categorize(text),
        aiApi.duplicates(title, description),
      ]);
      return { categorize: categorizeResult, duplicates: dupes ?? [] };
    },
    onSuccess: (data) => {
      if (data.categorize.categoryId) setCategoryId(data.categorize.categoryId);
      else if (data.categorize.category) {
        const match = (categories as any[]).find(
          (c: any) => c.name?.toLowerCase() === data.categorize.category?.toLowerCase()
        );
        if (match) setCategoryId(match.id ?? match._id ?? '');
      }
      if (data.categorize.priority) setPriority(data.categorize.priority as TicketPriority);
      if (data.categorize.tags?.length) setTags(data.categorize.tags);
      setDuplicates(data.duplicates);
      toast.success('AI suggestions applied');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => ticketsApi.create(data as any),
    onSuccess: (ticket) => {
      clearFormDraft(draftKey);
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      toast.success('Ticket created');
      const id = (ticket as any).id ?? (ticket as any)._id;
      navigate(id ? `/tickets/${id}` : '/tickets');
    },
    onError: (err: Error) => toast.error(err.message || 'Failed to create ticket'),
  });

  // Staff only — the endpoint is requireTechnician (it can name tickets a
  // customer must not see), so for a customer this is a 403 on every blur.
  const checkDuplicates = useCallback(() => {
    if (!isStaff || !title.trim()) return;
    aiApi.duplicates(title, description).then(setDuplicates).catch(() => setDuplicates([]));
  }, [isStaff, title, description]);

  const addTag = () => {
    const t = tagInput.trim();
    if (t && !tags.includes(t)) setTags([...tags, t]);
    setTagInput('');
  };

  const setCustomValue = (slug: string, value: string) => {
    setCustomFieldValues(prev => ({ ...prev, [slug]: value }));
  };

  // Screenshot pasted while typing in the description: put the image inline at
  // the caret (placeholder now, real ![name](url) ref when the upload lands).
  // Pastes landing anywhere else keep the old behavior — attachment chip only.
  const handlePasteImageStart = (id: string, file: File) => {
    const el = descriptionRef.current;
    if (!el || document.activeElement !== el) return;
    setDescription(insertUploadingPlaceholder(el, description, id, file.name));
  };

  const handlePasteImageDone = (id: string, att: UploadedAttachment | null) => {
    setDescription(resolveUploadingPlaceholder(descriptionRef.current, description, id, att));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !description.trim()) {
      toast.error('Title and description are required');
      setStep('details');
      return;
    }
    if (hasPendingInlineUploads(description)) {
      toast.error('A pasted screenshot is still uploading — one moment');
      return;
    }

    const customFields: Record<string, any> = {};
    for (const { def, isRequired } of activeCustomFields) {
      const val = customFieldValues[def.slug] ?? '';
      if (isRequired && !val) {
        toast.error(`"${def.label}" is required`);
        setValidationTab('custom');
        setStep('more');
        return;
      }
      if (val) {
        customFields[def.slug] = def.fieldType === 'number' ? parseFloat(val) :
                                  def.fieldType === 'boolean' ? val === 'true' : val;
      }
    }

    createMutation.mutate({
      title: title.trim(),
      description: description.trim(),
      priority, type, source,
      ...(categoryId          && { categoryId }),
      ...(productLineId       && { productLineId }),
      // undefined = let the server pick the auto-apply template; null = none.
      ...(taskTemplateChoice !== 'auto' && { taskTemplateId: taskTemplateChoice === 'none' ? null : taskTemplateChoice }),
      ...(tags.length > 0     && { tags }),
      ...(show('impact') && impact              && { impact }),
      ...(show('environment') && environment    && { environment }),
      ...(show('product') && product.trim()     && { product: product.trim() }),
      ...(show('productVersion') && productVersion.trim() && { productVersion: productVersion.trim() }),
      ...(show('department') && department.trim() && { department: department.trim() }),
      ...(show('location') && location.trim()   && { location: location.trim() }),
      ...(show('dueDate') && dueDate            && { dueDate }),
      ...(show('estimatedHours') && estimatedHours && { estimatedHours: parseFloat(estimatedHours) }),
      ...(show('browser') && browser.trim()     && { browser: browser.trim() }),
      ...(show('device') && device.trim()       && { device: device.trim() }),
      ...(show('operatingSystem') && operatingSystem.trim() && { operatingSystem: operatingSystem.trim() }),
      ...(show('reproducibility') && reproducibility && { reproducibility }),
      ...(show('stepsToReproduce') && stepsToReproduce.trim() && { stepsToReproduce: stepsToReproduce.trim() }),
      ...(assigneeIds.length > 0 && can('assign', 'tickets') && {
        assignedToId: assigneeIds[0],
        coAssigneeIds: assigneeIds.slice(1),
      }),
      ...(assignedTeamId && can('assign', 'tickets') && { assignedTeamId }),
      ...(contactId           && { contactId }),
      ...(crmContactId        && { crmContactId }),
      ...(crmOrganizationId   && { crmOrganizationId }),
      ...(reporterEmail.trim() && { reporterEmail: reporterEmail.trim() }),
      ...(reporterName.trim() && { reporterName: reporterName.trim() }),
      notifyCustomer,
      notifyCreator,
      emailIncludeContent: effectiveIncludeContent,
      ...(ccList.length > 0   && { ccRecipients: ccList }),
      ...(Object.keys(customFields).length > 0 && { customFields }),
      ...(attachments.length > 0 && {
        attachments: attachments.map(a => ({
          filename: a.filename,
          url: a.url,
          size: a.size,
          mimeType: a.mimeType,
        })),
      }),
    });
  };

  // ── Section blocks ────────────────────────────────────────────────────────
  // Defined once, placed by whichever layout is active. Nothing below renders
  // its own card/section chrome — each layout supplies that — so the classic
  // and guided forms are literally the same fields.

  const detailsFields = (
    <>
      <FieldGroup label="Title" required>
        <input
          type="text" className="input"
          placeholder="Brief summary of the issue"
          value={title}
          onChange={(e) => { setTitle(e.target.value); setDuplicates([]); }}
          onBlur={checkDuplicates}
          required
        />
      </FieldGroup>

      <FieldGroup label="Description" required>
        <textarea
          ref={descriptionRef}
          className="input min-h-[140px] resize-y"
          placeholder="Describe the issue in detail… (paste a screenshot to add it inline)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          required
        />
      </FieldGroup>

      {/* AI + duplicates */}
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button" className="btn btn-secondary text-sm"
          onClick={() => aiSuggestMutation.mutate()}
          disabled={aiSuggestMutation.isPending || !title.trim() || !description.trim()}
        >
          {aiSuggestMutation.isPending
            ? <Loader2 className="w-4 h-4 animate-spin" />
            : <Sparkles className="w-4 h-4 text-primary" />}
          AI suggestions
        </button>
        {duplicates.length > 0 && (
          <span className="flex items-center gap-1.5 text-warn text-sm">
            <AlertTriangle className="w-4 h-4" />
            {duplicates.length} similar ticket{duplicates.length !== 1 ? 's' : ''} found
          </span>
        )}
      </div>

      <FieldGroup label="Tags">
        <div className="flex gap-2 flex-wrap items-center">
          {tags.map((t) => (
            <span key={t} className="inline-flex items-center gap-1 badge bg-primary-muted text-primary text-xs">
              {t}
              <button type="button" onClick={() => setTags(tags.filter(x => x !== t))}>
                <X className="w-3 h-3 hover:text-err" />
              </button>
            </span>
          ))}
          <div className="flex gap-1">
            <input type="text" className="input w-28 text-sm py-1" placeholder="Add tag" value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())} />
            <button type="button" className="btn btn-ghost text-sm py-1" onClick={addTag}>Add</button>
          </div>
        </div>
      </FieldGroup>
    </>
  );

  // Always mounted (hidden when its panel is not showing) so paste-to-attach
  // and inline-image placeholders keep working while the description is typed.
  const attachmentsUploader = (
    <FileUploader
      attachments={attachments}
      onChange={setAttachments}
      context="ticket"
      enablePaste
      onPasteImageStart={handlePasteImageStart}
      onPasteImageDone={handlePasteImageDone}
    />
  );

  const technicalFields = (
    <div className="space-y-4">
      {(show('browser') || show('device') || show('operatingSystem')) && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {show('browser') && (
            <FieldGroup label="Browser">
              <input type="text" className="input" placeholder="e.g. Chrome 120" value={browser} onChange={e => setBrowser(e.target.value)} />
            </FieldGroup>
          )}
          {show('device') && (
            <FieldGroup label="Device">
              <input type="text" className="input" placeholder="e.g. iPhone 15" value={device} onChange={e => setDevice(e.target.value)} />
            </FieldGroup>
          )}
          {show('operatingSystem') && (
            <FieldGroup label="Operating System">
              <input type="text" className="input" placeholder="e.g. iOS 17" value={operatingSystem} onChange={e => setOperatingSystem(e.target.value)} />
            </FieldGroup>
          )}
        </div>
      )}
      {show('reproducibility') && (
        <FieldGroup label="Reproducibility">
          <select className="select" value={reproducibility} onChange={e => setReproducibility(e.target.value)}>
            <option value="">Select</option>
            <option value="always">Always</option>
            <option value="sometimes">Sometimes</option>
            <option value="once">Once</option>
            <option value="n_a">N/A</option>
          </select>
        </FieldGroup>
      )}
      {show('stepsToReproduce') && (
        <FieldGroup label="Steps to reproduce">
          <textarea className="input min-h-[80px] resize-y font-mono text-xs" placeholder={"1. Go to…\n2. Click on…\n3. Observe…"} value={stepsToReproduce} onChange={e => setStepsToReproduce(e.target.value)} />
        </FieldGroup>
      )}
    </div>
  );

  const customFieldsGrid = activeCustomFields.length > 0 ? (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {activeCustomFields.map(({ def, isRequired }) => {
        const fullWidth = def.fieldType === 'textarea';
        return (
          <div key={def.id} className={fullWidth ? 'sm:col-span-2' : ''}>
            <CustomFieldInput
              def={def}
              value={customFieldValues[def.slug] ?? ''}
              onChange={(v) => setCustomValue(def.slug, v)}
              isRequired={isRequired}
            />
          </div>
        );
      })}
    </div>
  ) : null;

  const duplicatesCard = duplicates.length > 0 ? (
    <div className="card" style={{ borderColor: 'var(--scpa-status-warning)' }}>
      <p className="text-sm font-medium text-warn mb-2 flex items-center gap-2">
        <AlertTriangle className="w-4 h-4" /> Similar tickets found
      </p>
      <ul className="text-sm space-y-1">
        {duplicates.slice(0, 3).map((d: any) => (
          <li key={d.id} className="flex items-center gap-2">
            <a href={`/tickets/${d.id}`} className="text-primary hover:underline">{d.ticketNumber} — {d.title}</a>
            <span className="text-muted text-xs">{d.status}</span>
          </li>
        ))}
      </ul>
    </div>
  ) : null;

  const productLineFields = (
    <>
      <select
        className="select text-sm"
        value={productLineId}
        onChange={e => { setProductLineId(e.target.value); setCustomFieldValues({}); }}
      >
        <option value="">— Select a product line —</option>
        {(productLines as ProductLine[]).map(pl => (
          <option key={pl.id} value={pl.id}>{pl.name}</option>
        ))}
      </select>
      {selectedProductLine && (
        <p className="text-xs text-muted mt-1.5">
          Showing fields for <span className="font-medium" style={{ color: selectedProductLine.color }}>{selectedProductLine.name}</span>
        </p>
      )}
      {isAdmin && (
        <QuickAdd
          noun="product line"
          create={name => productLinesApi.create({ name })}
          onCreated={pl => {
            addCreatedToCache(queryClient, { keyPrefix: 'product-lines', item: pl });
            setProductLineId(pl.id);
            setCustomFieldValues({});
          }}
        />
      )}
    </>
  );

  const classificationFields = (
    <>
      <FieldGroup label="Type">
        <select className="select text-sm" value={type} onChange={e => setType(e.target.value)}>
          {typeOptions.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
        </select>
      </FieldGroup>
      {/* Sub-ticket checklist to start with — defaults to the template
          that matches the type / product line, if any. */}
      {(taskTemplates as any[]).some(t => t.isActive) && (
        <FieldGroup label="Checklist">
          <select
            className="select text-sm"
            value={taskTemplateChoice}
            onChange={e => setTaskTemplateChoice(e.target.value)}
            title="Sub-ticket tasks the new ticket starts with (Company Settings → Task Templates)"
          >
            <option value="auto">
              {matchedTemplate
                ? `Auto: ${matchedTemplate.name} (${matchedTemplate.items.length} task${matchedTemplate.items.length === 1 ? '' : 's'})`
                : 'Auto: none matches this type'}
            </option>
            <option value="none">No checklist</option>
            {(taskTemplates as any[]).filter(t => t.isActive && t.items.length > 0).map(t => (
              <option key={t.id} value={t.id}>{t.name} ({t.items.length} task{t.items.length === 1 ? '' : 's'})</option>
            ))}
          </select>
        </FieldGroup>
      )}
      <FieldGroup label="Priority">
        <select className="select text-sm" value={priority} onChange={e => setPriority(e.target.value)}>
          {priorityOptions.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
        </select>
      </FieldGroup>
      <FieldGroup label="Category">
        <select className="select text-sm" value={categoryId} onChange={e => setCategoryId(e.target.value)}>
          <option value="">No category</option>
          {(categories as any[]).map((c: any) => (
            <option key={c.id ?? c._id} value={c.id ?? c._id ?? ''}>{c.name}</option>
          ))}
        </select>
        {isAdmin && (
          <QuickAdd
            noun="category"
            create={name => categoriesApi.create({ name })}
            onCreated={c => {
              addCreatedToCache(queryClient, { keyPrefix: 'categories', item: c });
              setCategoryId((c as any).id ?? (c as any)._id ?? '');
            }}
          />
        )}
      </FieldGroup>
      <FieldGroup label="Source">
        <select className="select text-sm" value={source} onChange={e => setSource(e.target.value)}>
          {sourceOptions.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
        </select>
      </FieldGroup>
      {show('impact') && (
        <FieldGroup label="Business impact">
          <select className="select text-sm" value={impact} onChange={e => setImpact(e.target.value)}>
            <option value="">No impact selected</option>
            {impactOptions.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
          </select>
        </FieldGroup>
      )}
      {show('environment') && (
        <FieldGroup label="Environment">
          <select className="select text-sm" value={environment} onChange={e => setEnvironment(e.target.value)}>
            <option value="">Not specified</option>
            {envOptions.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
          </select>
        </FieldGroup>
      )}
    </>
  );

  const productScheduleFields = (
    <>
      {show('product') && (
        <FieldGroup label="Product / Service">
          <input type="text" className="input text-sm" placeholder="e.g. Mobile App" value={product} onChange={e => setProduct(e.target.value)} />
        </FieldGroup>
      )}
      {show('productVersion') && (
        <FieldGroup label="Version">
          <input type="text" className="input text-sm" placeholder="e.g. 2.1.0" value={productVersion} onChange={e => setProductVersion(e.target.value)} />
        </FieldGroup>
      )}
      {show('department') && (
        <FieldGroup label="Department">
          <input type="text" className="input text-sm" placeholder="Customer department" value={department} onChange={e => setDepartment(e.target.value)} />
        </FieldGroup>
      )}
      {show('location') && (
        <FieldGroup label="Location">
          <input type="text" className="input text-sm" placeholder="Physical location" value={location} onChange={e => setLocation(e.target.value)} />
        </FieldGroup>
      )}
      {show('dueDate') && (
        <FieldGroup label="Due date">
          <input type="date" className="input text-sm" value={dueDate} onChange={e => setDueDate(e.target.value)} />
        </FieldGroup>
      )}
      {show('estimatedHours') && (
        <FieldGroup label="Estimated hours">
          <input type="number" step="0.5" min="0" className="input text-sm" placeholder="e.g. 2.5" value={estimatedHours} onChange={e => setEstimatedHours(e.target.value)} />
        </FieldGroup>
      )}
    </>
  );

  const assignmentFields = (
    <>
      <FieldGroup label="Assignees (first = primary)">
        <AssigneePicker
          members={members as any[]}
          selectedIds={assigneeIds}
          onChange={setAssigneeIds}
          teamMemberIds={selectedTeamMemberIds as Set<string>}
        />
      </FieldGroup>
      <FieldGroup label="Team">
        <select
          className="select text-sm"
          value={assignedTeamId}
          onChange={e => setAssignedTeamId(e.target.value)}
          title={assignedTeamId ? `Members: ${teamMemberNames(assignedTeamId).join(', ') || 'none'}` : undefined}
        >
          <option value="">No team</option>
          {(teams as any[]).map((t: any) => (
            <option key={t.id} value={t.id} title={`Members: ${teamMemberNames(t.id).join(', ') || 'none'}`}>
              {t.name}
            </option>
          ))}
        </select>
        {assignedTeamId && (
          <p className="mt-1 text-xs text-muted">
            Team members: {teamMemberNames(assignedTeamId).join(', ') || 'no members'}.
            Everyone on the team is emailed. Picking someone individually too does not email them twice.
          </p>
        )}
        {isAdmin && (
          <QuickAdd
            noun="team"
            create={name => teamsApi.create({ name })}
            onCreated={t => {
              addCreatedToCache(queryClient, { keyPrefix: 'teams', item: t });
              setAssignedTeamId(t.id);
            }}
          />
        )}
      </FieldGroup>
      <FieldGroup label="On behalf of (staff member)">
        <select className="select text-sm" value={contactId} onChange={e => setContactId(e.target.value)}>
          <option value="">Same as creator</option>
          {(members as any[])
            .filter((m: any) => ['owner', 'admin', 'technician'].includes(m.role))
            .map((m: any) => (
              <option key={m.user?.id} value={m.user?.id}>
                {m.user?.name ? `${m.user.name} (${m.user.email})` : m.user?.email}
              </option>
            ))}
        </select>
        <p className="mt-1 text-[11px] text-muted">
          Optional — a staff member you are raising this for. The customer goes under Customer.
        </p>
      </FieldGroup>
    </>
  );

  const requesterFields = (
    <>
      <FieldGroup label="Organization">
        <select className="select text-sm" value={crmOrganizationId} onChange={e => setCrmOrganizationId(e.target.value)}>
          <option value="">— None —</option>
          {organizations.map((o: any) => (
            <option key={o.id} value={o.id}>{o.name}</option>
          ))}
        </select>
        <QuickAdd
          noun="organization"
          create={name => crmOrgsApi.create({ name })}
          onCreated={o => {
            addCreatedToCache(queryClient, {
              keyPrefix: 'crm-orgs',
              listField: 'organizations',
              item: o,
            });
            invalidateCrm(queryClient);
            setCrmOrganizationId(o.id);
          }}
        />
      </FieldGroup>
      <FieldGroup label="Reporter (CRM contact)">
        <select className="select text-sm" value={crmContactId} onChange={e => setCrmContactId(e.target.value)}>
          <option value="">— None —</option>
          {crmContacts.map((c: any) => {
            const name = [c.firstName, c.lastName].filter(Boolean).join(' ');
            return (
              <option key={c.id} value={c.id}>
                {name || c.email || 'Unnamed contact'}{c.email ? ` — ${c.email}` : ''}
              </option>
            );
          })}
        </select>
        <p className="text-[11px] text-muted mt-1">Pick a CRM contact when the reporter is on file.</p>
        <button
          type="button"
          onClick={() => setShowNewContact(v => !v)}
          className="mt-1 flex items-center gap-1 text-xs text-primary hover:underline"
        >
          <ChevronDown className={`w-3 h-3 transition-transform ${showNewContact ? 'rotate-180' : ''}`} />
          Add new contact
        </button>
        {showNewContact && (
          <NewContactForm
            organizationId={crmOrganizationId || undefined}
            onCreated={(contact) => {
              setShowNewContact(false);
              addCreatedToCache(queryClient, {
                keyPrefix: 'crm-contacts',
                listField: 'contacts',
                item: contact,
              });
              invalidateCrm(queryClient);
              setCrmContactId(contact.id);
            }}
            onCancel={() => setShowNewContact(false)}
          />
        )}
      </FieldGroup>
      {!crmContactId && (
        <>
          <FieldGroup label="Reporter email (one-off)">
            <input type="email" className="input text-sm" placeholder="someone@example.com" value={reporterEmail} onChange={e => setReporterEmail(e.target.value)} />
          </FieldGroup>
          <FieldGroup label="Reporter name">
            <input type="text" className="input text-sm" placeholder="Name" value={reporterName} onChange={e => setReporterName(e.target.value)} />
          </FieldGroup>
        </>
      )}
    </>
  );

  const notificationsFields = (
    <>
      <label className="flex items-start gap-2 cursor-pointer">
        <input
          type="checkbox"
          className="w-4 h-4 mt-0.5 accent-[var(--scpa-primary)]"
          checked={notifyCustomer}
          onChange={e => setNotifyCustomer(e.target.checked)}
        />
        <span className="text-sm">
          <span className="font-medium">Email the customer</span>
          <span className="block text-[11px] text-muted mt-0.5">
            Short updates with a link to the ticket, on create, replies and status changes. Internal notes are never sent.
          </span>
        </span>
      </label>

      <label className="flex items-start gap-2 cursor-pointer mt-3">
        <input
          type="checkbox"
          className="w-4 h-4 mt-0.5 accent-[var(--scpa-primary)]"
          checked={notifyCreator}
          onChange={e => setNotifyCreator(e.target.checked)}
        />
        <span className="text-sm">
          <span className="font-medium">Email me about updates</span>
          <span className="block text-[11px] text-muted mt-0.5">
            You get the same emails as an assignee. Your own actions are never sent back to you.
          </span>
        </span>
      </label>

      <label className="flex items-start gap-2 cursor-pointer mt-3">
        <input
          type="checkbox"
          className="w-4 h-4 mt-0.5 accent-[var(--scpa-primary)]"
          checked={effectiveIncludeContent}
          onChange={e => { setEmailIncludeContentTouched(true); setEmailIncludeContent(e.target.checked); }}
        />
        <span className="text-sm">
          <span className="font-medium">Put the update text in those emails</span>
          <span className="block text-[11px] mt-0.5" style={{ color: 'var(--scpa-status-warning)' }}>
            Emails carry the reply text itself, not just a link. Only for tickets that can never hold patient data.
          </span>
          {!emailIncludeContentTouched && contentDefaultMembers.length > 0 && (
            <span className="block text-[11px] text-muted mt-0.5">
              On by default for {contentDefaultMembers.map((m: any) => m.user?.name || m.user?.email).join(', ')} (Company Settings → Members). Untick to send links only.
            </span>
          )}
        </span>
      </label>

      <div className="mt-3">
        <p className="label">CC additional people</p>
        {ccList.length > 0 && (
          <ul className="space-y-1 mb-2">
            {ccList.map(cc => (
              <li key={cc.email} className="flex items-center gap-2 text-xs bg-row-odd rounded-md px-2 py-1.5">
                <span className="flex-1 truncate">
                  {cc.name ? <><span className="font-medium">{cc.name}</span> &lt;{cc.email}&gt;</> : cc.email}
                </span>
                <button type="button" onClick={() => removeCc(cc.email)} className="text-muted hover:text-err">
                  <X className="w-3.5 h-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className="space-y-1.5">
          <input
            type="email"
            className="input text-sm"
            placeholder="email@example.com"
            value={ccEmailInput}
            onChange={e => setCcEmailInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addCc())}
          />
          <div className="flex gap-1.5">
            <input
              type="text"
              className="input text-sm flex-1"
              placeholder="Name (optional)"
              value={ccNameInput}
              onChange={e => setCcNameInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addCc())}
            />
            <button type="button" className="btn btn-ghost text-sm py-1" onClick={addCc}>Add CC</button>
          </div>
        </div>
      </div>
    </>
  );

  // ── Guided steps ──────────────────────────────────────────────────────────
  // Only steps with something to show are offered, so a customer-role user
  // never pages through empty staff panels.
  const hasMoreStep = showProductSection || showTechSection || activeCustomFields.length > 0;
  const steps: { id: StepId; label: string; hint: string }[] = [
    { id: 'details',  label: 'Details',  hint: 'What happened' },
    { id: 'classify', label: 'Classify', hint: 'Type and priority' },
    ...(showStaffFields ? [{ id: 'people' as StepId, label: 'People', hint: 'Who it is for' }] : []),
    ...(hasMoreStep ? [{ id: 'more' as StepId, label: 'More', hint: 'Product and technical' }] : []),
    { id: 'notify',   label: 'Notify',   hint: 'Who gets emailed' },
  ];
  const stepIndex = Math.max(0, steps.findIndex(s => s.id === step));
  const currentStep = steps[stepIndex] ?? steps[0];
  const prevStep = steps[stepIndex - 1];
  const nextStep = steps[stepIndex + 1];
  const guided = layout === 'guided';

  const layoutToggle = (
    <div className="inline-flex rounded-lg overflow-hidden" style={{ border: '1px solid var(--scpa-border)' }}>
      {(['classic', 'guided'] as const).map(opt => (
        <button
          key={opt}
          type="button"
          onClick={() => setLayout(opt)}
          title={opt === 'classic' ? 'Everything on one page' : 'One step at a time'}
          style={{
            fontSize: 11,
            fontWeight: 600,
            padding: '5px 10px',
            border: 'none',
            cursor: 'pointer',
            background: layout === opt ? 'var(--scpa-primary)' : 'transparent',
            color: layout === opt ? '#fff' : 'var(--scpa-text-muted)',
          }}
        >
          {opt === 'classic' ? 'One page' : 'Step by step'}
        </button>
      ))}
    </div>
  );

  /** A guided panel. Kept mounted and hidden so typed values and the paste
   *  handler survive moving between steps. */
  const panel = (id: StepId, children: React.ReactNode) => (
    <div className={guided && step === id ? 'space-y-4' : 'hidden'}>{children}</div>
  );

  return (
    <div className="flex flex-col gap-4 pb-10">

      <PageHeader
        title="New ticket"
        intro="Describe the issue, classify it, and assign it. Required fields are marked with an asterisk."
        actions={
          <>
            {layoutToggle}
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => { clearFormDraft(draftKey); navigate('/tickets'); }}
            >
              Cancel
            </button>
            <button form="ticket-create-form" type="submit" className="btn btn-primary" disabled={createMutation.isPending}>
              {createMutation.isPending ? 'Creating…' : 'Create ticket'}
            </button>
          </>
        }
      />

      {draftRestored && (
        <div className="card border-dashed border-line flex flex-wrap items-center justify-between gap-3" style={{ paddingTop: 12, paddingBottom: 12 }}>
          <p className="text-sm text-muted">
            Picked up where you left off — this ticket was still unsaved.
          </p>
          <button type="button" className="btn btn-ghost text-sm py-1" onClick={resetForm}>
            Start fresh
          </button>
        </div>
      )}

      {/* noValidate in guided mode: a required field on a hidden step cannot be
          focused, so the browser would block submit with no visible message.
          handleSubmit checks the same things and jumps to the step at fault. */}
      <form
        id="ticket-create-form"
        onSubmit={handleSubmit}
        noValidate={guided}
        className={guided ? 'flex flex-col gap-4' : 'flex flex-col lg:flex-row gap-4'}
      >
        {guided ? (
          <>
            {/* ── Step bar ───────────────────────────────────────────── */}
            <div className="card overflow-hidden" style={{ padding: 0 }}>
              <div className="flex overflow-x-auto">
                {steps.map((s, i) => {
                  const isOn = s.id === step;
                  const done = i < stepIndex;
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => setStep(s.id)}
                      className="flex items-center gap-2 px-4 py-3 shrink-0 transition-colors"
                      style={{
                        background: 'none',
                        border: 'none',
                        borderBottom: `2px solid ${isOn ? 'var(--scpa-primary)' : 'transparent'}`,
                        cursor: 'pointer',
                      }}
                    >
                      <span
                        className="w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center shrink-0"
                        style={{
                          background: isOn || done ? 'var(--scpa-primary)' : 'var(--scpa-surface-alt)',
                          color: isOn || done ? '#fff' : 'var(--scpa-text-muted)',
                        }}
                      >
                        {i + 1}
                      </span>
                      <span className="text-left">
                        <span
                          className="block text-xs font-semibold"
                          style={{ color: isOn ? 'var(--scpa-primary)' : 'var(--scpa-text-muted)' }}
                        >
                          {s.label}
                        </span>
                        <span className="hidden sm:block text-[10px] text-muted">{s.hint}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* ── Step panels ────────────────────────────────────────── */}
            <div className="card" style={{ minHeight: 320 }}>
              {panel('details', (
                <>
                  {detailsFields}
                  <div>
                    <p className="label" title={fieldHelp('Attachments')}>
                      Attachments
                      <HelpCircle className="w-3 h-3 inline-block ml-1 -mt-0.5 opacity-50" aria-hidden />
                    </p>
                    {attachmentsUploader}
                  </div>
                  {duplicatesCard}
                </>
              ))}

              {panel('classify', (
                <>
                  {hasProductLines && (
                    <FieldGroup label="Product line">{productLineFields}</FieldGroup>
                  )}
                  {classificationFields}
                </>
              ))}

              {showStaffFields && panel('people', (
                <>
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-muted">Customer</p>
                  {requesterFields}
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-muted pt-2">Assignment</p>
                  {assignmentFields}
                </>
              ))}

              {hasMoreStep && panel('more', (
                <>
                  {showProductSection && productScheduleFields}
                  {showTechSection && technicalFields}
                  {customFieldsGrid}
                </>
              ))}

              {panel('notify', notificationsFields)}

              {/* Attachments stay mounted on every step so a screenshot pasted
                  while typing still lands on the ticket. */}
              {step !== 'details' && <div className="hidden">{attachmentsUploader}</div>}
            </div>

            {/* ── Step nav ───────────────────────────────────────────── */}
            <div className="flex items-center justify-between gap-2">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => prevStep && setStep(prevStep.id)}
                style={{ visibility: prevStep ? 'visible' : 'hidden' }}
              >
                Back
              </button>
              <span className="text-xs text-muted">
                Step {stepIndex + 1} of {steps.length} — {currentStep?.label}
              </span>
              {nextStep ? (
                <button type="button" className="btn btn-primary" onClick={() => setStep(nextStep.id)}>
                  Next: {nextStep.label}
                </button>
              ) : (
                <button type="submit" className="btn btn-primary" disabled={createMutation.isPending}>
                  {createMutation.isPending ? 'Creating…' : 'Create ticket'}
                </button>
              )}
            </div>
          </>
        ) : (
          <>
            {/* ── LEFT: main fields ─────────────────────────────────────── */}
            <div className="flex-1 min-w-0 space-y-4">

              {/* Details — always visible */}
              <div className="card overflow-hidden" style={{ padding: 0 }}>
                <div className="px-5 py-3.5 border-b border-line-light">
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted">Details</p>
                </div>
                <div className="px-5 py-4 space-y-4">
                  {detailsFields}
                </div>
              </div>

              {/* Optional hint: product lines tailor the visible fields */}
              {hasProductLines && !selectedProductLine && (
                <div className="card border-dashed border-line" style={{ paddingTop: 12, paddingBottom: 12 }}>
                  <p className="text-sm text-muted flex items-center gap-2">
                    <Columns className="w-4 h-4 shrink-0" />
                    Tip: pick a <span className="font-medium text-fg">product line</span> in the sidebar to show the fields for that product.
                  </p>
                </div>
              )}

              {/* ── Tabbed card: Attachments / Technical / Custom Fields ── */}
              {tabs.length > 0 && (
                <div className="card overflow-hidden" style={{ padding: 0 }}>
                  {/* Tab bar */}
                  <div className="flex border-b border-line-light">
                    {tabs.map(tab => {
                      const Icon = tab.icon;
                      const isActive = activeTab === tab.id;
                      return (
                        <button
                          key={tab.id}
                          type="button"
                          onClick={() => setActiveTab(tab.id)}
                          className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-medium transition-colors border-b-2 -mb-px ${
                            isActive
                              ? 'border-primary text-primary'
                              : 'border-transparent text-muted hover:text-fg'
                          }`}
                        >
                          <Icon className="w-3.5 h-3.5" />
                          {tab.label}
                          {tab.badge !== undefined && tab.badge > 0 && (
                            <span className="text-[10px] bg-primary-muted text-primary rounded-full px-1.5 py-0.5 font-medium ml-0.5">
                              {tab.badge}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>

                  {/* Tab panels */}
                  <div className="px-5 py-4">
                    {/* Attachments panel — kept mounted (hidden) on other tabs so
                        paste-to-attach/inline works while typing the description. */}
                    <div className={activeTab === 'attachments' ? '' : 'hidden'}>
                      {attachmentsUploader}
                    </div>

                    {activeTab === 'technical' && technicalFields}

                    {activeTab === 'custom' && customFieldsGrid}
                  </div>
                </div>
              )}

              {/* Similar tickets warning */}
              {duplicatesCard}
            </div>

            {/* ── RIGHT: properties sidebar (full-width below lg) ────────── */}
            <aside className="w-full lg:w-72 xl:w-80 shrink-0 space-y-4">
              {hasProductLines && (
                <CollapsibleSection
                  id="productLine"
                  title="Product line"
                  isOpen={openSections.productLine}
                  onToggle={toggleSection}
                >
                  {productLineFields}
                </CollapsibleSection>
              )}

              <CollapsibleSection
                id="classification"
                title="Classification"
                isOpen={openSections.classification}
                onToggle={toggleSection}
              >
                {classificationFields}
              </CollapsibleSection>

              {showProductSection && (
                <CollapsibleSection
                  id="productSchedule"
                  title="Product & schedule"
                  isOpen={openSections.productSchedule}
                  onToggle={toggleSection}
                >
                  {productScheduleFields}
                </CollapsibleSection>
              )}

              {showStaffFields && (
                <CollapsibleSection
                  id="assignment"
                  title="Assignment"
                  isOpen={openSections.assignment}
                  onToggle={toggleSection}
                >
                  {assignmentFields}
                </CollapsibleSection>
              )}

              {showStaffFields && (
                <CollapsibleSection
                  id="requester"
                  title="Customer"
                  isOpen={openSections.requester}
                  onToggle={toggleSection}
                >
                  {requesterFields}
                </CollapsibleSection>
              )}

              <CollapsibleSection
                id="notifications"
                title="Notifications"
                isOpen={openSections.notifications}
                onToggle={toggleSection}
                badge={ccList.length || undefined}
              >
                {notificationsFields}
              </CollapsibleSection>
            </aside>
          </>
        )}
      </form>
    </div>
  );
}
